import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Fab,
  Zoom,
  useScrollTrigger,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import ContentShell from "components/ContentShell";
import { useSnackbar } from "hooks/useSnackbar";
import {
  getWeeklyLeaderboardConfig,
  updateWeeklyLeaderboardConfig,
} from "services/weeklyLeaderboardService";
import { getPlayerPacks } from "services/packServices";
import BasicConfigurationEditor from "./components/BasicConfigurationEditor";
import DivisionMultipliersEditor from "./components/DivisionMultipliersEditor";
import LeagueConfigurationEditor from "./components/LeagueConfigurationEditor";
import ConfigurationSummaryCards from "./components/ConfigurationSummaryCards";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
    maxWidth: "1400px",
    margin: "0 auto",
  },
  pageHeader: {
    marginBottom: "32px",
    textAlign: "center",
  },
  pageTitle: {
    fontWeight: 700,
    background: "linear-gradient(45deg, #1976d2, #42a5f5)",
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    marginBottom: "8px",
  },
  pageDescription: {
    color: theme.palette.text.secondary,
    maxWidth: "600px",
    margin: "0 auto",
  },
  accordion: {
    marginBottom: "20px",
    borderRadius: "12px !important",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1) !important",
    border: "1px solid #e0e0e0 !important",
    "&:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: "0 0 20px 0 !important",
    },
  },
  accordionSummary: {
    backgroundColor: "#f8f9fa",
    borderRadius: "12px 12px 0 0",
    minHeight: "72px",
    "&.Mui-expanded": {
      minHeight: "72px",
      borderRadius: "12px 12px 0 0",
    },
  },
  accordionTitle: {
    fontWeight: 700,
    fontSize: "1.2rem",
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  accordionSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    marginTop: "4px",
  },
  saveButton: {
    minWidth: "140px",
    height: "48px",
    borderRadius: "24px",
    fontWeight: 600,
    fontSize: "1rem",
    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
    "&:hover": {
      boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
      transform: "translateY(-1px)",
    },
  },
  floatingActionButton: {
    position: "fixed",
    bottom: theme.spacing(4),
    right: theme.spacing(4),
    zIndex: 1000,
  },
  errorAlert: {
    marginBottom: "24px",
    borderRadius: "12px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
  },
  configSection: {
    marginBottom: "24px",
  },
}));

const WeeklyLeaderboardConfig = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    matchWinXP: 10,
    vaultXPMultiplier: 1,
    divisionMultipliers: {
      1: 2,
      2: 1.75,
      3: 1.75,
      4: 1.5,
      5: 1.5,
      6: 1.5,
      7: 1.25,
      8: 1.25,
      9: 1.25,
      10: 1,
    },
    botBaseXp: {
      STARTER: 100,
      BRONZE: 200,
      BRONZE_II: 300,
      SILVER: 450,
      SILVER_II: 600,
      GOLD: 800,
      GOLD_II: 1000,
      DIAMOND: 1300,
      DIAMOND_II: 1600,
      PLATINUM: 2000,
    },
    leagueConfig: [],
  });
  const [rewardPackOptions, setRewardPackOptions] = useState([]);
  const [errors, setErrors] = useState({
    basic: [],
    divisionMultipliers: [],
    leagueConfig: [],
  });
  const [isDirty, setIsDirty] = useState(false);
  const [expandedSection, setExpandedSection] = useState("summary");
  const [isEditMode, setIsEditMode] = useState(false);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await getWeeklyLeaderboardConfig();
      if (response.data) {
        setConfig(response.data);
      }
    } catch (error) {
      showSnackbar(
        error.message || "Failed to load weekly leaderboard configuration",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchRewardPacks = async () => {
    try {
      const response = await getPlayerPacks({
        pageNo: 1,
        pageSize: 200,
        sortBy: "name",
        sortDir: "ASC",
      });

      const options =
        response.items?.map((pack) => ({
          id: pack._id,
          label: `${pack.name} - ${pack.type}`,
          value: pack._id,
        })) || [];

      setRewardPackOptions(options);
    } catch (error) {
      showSnackbar(error.message || "Failed to load reward packs", "error");
    }
  };

  const validateConfig = () => {
    const newErrors = {
      basic: [],
      divisionMultipliers: [],
      leagueConfig: [],
    };

    // Validate basic configuration
    if (!config.matchWinXP || config.matchWinXP < 0) {
      newErrors.basic.push("Match Win XP must be a positive number");
    }

    if (!config.vaultXPMultiplier || config.vaultXPMultiplier < 0) {
      newErrors.basic.push("Vault XP Multiplier must be a positive number");
    }

    if ((config as any).activeUserTimeMin !== undefined && (config as any).activeUserTimeMin < 0) {
      newErrors.basic.push("Active User Time Window must be 0 or a positive number");
    }

    if ((config as any).inactivityMinutes !== undefined && (config as any).inactivityMinutes < 0) {
      newErrors.basic.push("Inactivity Removal Window must be 0 or a positive number");
    }

    if ((config as any).minMatchesRequired !== undefined && (config as any).minMatchesRequired < 1) {
      newErrors.basic.push("Minimum Matches Required must be at least 1");
    }

    // Validate division multipliers
    for (let i = 1; i <= 10; i++) {
      const multiplier = config.divisionMultipliers[i.toString()];
      if (!multiplier || multiplier < 0) {
        newErrors.divisionMultipliers.push(
          `Division ${i} multiplier must be a positive number`
        );
      }
    }

    // Validate league configurations
    if (!config.leagueConfig || !Array.isArray(config.leagueConfig)) {
      newErrors.leagueConfig.push("League configuration must be an array");
    } else {
      config.leagueConfig.forEach((leagueItem) => {
        const leagueName = leagueItem.name;
        const league = leagueItem.leagueConfig;

        if (!league) {
          newErrors.leagueConfig.push(
            `${leagueName}: League configuration is missing`
          );
          return;
        }

        // Validate rewards array
        if (
          !league.rewards ||
          !Array.isArray(league.rewards) ||
          league.rewards.length === 0
        ) {
          newErrors.leagueConfig.push(
            `${leagueName}: At least one reward configuration is required`
          );
        } else {
          league.rewards.forEach((reward, index) => {
            if (!reward.rewardPack) {
              newErrors.leagueConfig.push(
                `${leagueName}: Reward ${index + 1} - Reward pack is required`
              );
            }
            if (!reward.rewardPackCount || reward.rewardPackCount < 1) {
              newErrors.leagueConfig.push(
                `${leagueName}: Reward ${
                  index + 1
                } - Reward pack count must be at least 1`
              );
            }
          });
        }

        // Validate promotion positions are within range 1-20
        if (league.promotionPositions) {
          const invalidPromotions = league.promotionPositions.filter(
            (pos) => pos < 1 || pos > 20
          );
          if (invalidPromotions.length > 0) {
            newErrors.leagueConfig.push(
              `${leagueName}: Promotion positions must be between 1 and 20`
            );
          }
        }

        // Validate demotion positions are within range 1-20
        if (league.demotionPositions) {
          const invalidDemotions = league.demotionPositions.filter(
            (pos) => pos < 1 || pos > 20
          );
          if (invalidDemotions.length > 0) {
            newErrors.leagueConfig.push(
              `${leagueName}: Demotion positions must be between 1 and 20`
            );
          }
        }

        // Validate that promotion and demotion positions don't overlap
        if (league.promotionPositions && league.demotionPositions) {
          const promotionSet = new Set(league.promotionPositions);
          const demotionSet = new Set(league.demotionPositions);
          const overlap = [...promotionSet].filter((pos) =>
            demotionSet.has(pos)
          );

          if (overlap.length > 0) {
            newErrors.leagueConfig.push(
              `${leagueName}: Positions ${overlap.join(
                ", "
              )} cannot be in both promotion and demotion. Each position must be unique.`
            );
          }
        }
      });
    }

    setErrors(newErrors);
    return (
      newErrors.basic.length === 0 &&
      newErrors.divisionMultipliers.length === 0 &&
      newErrors.leagueConfig.length === 0
    );
  };

  const handleSave = async () => {
    if (!validateConfig()) {
      showSnackbar("Please fix validation errors before saving", "error");
      return;
    }

    setSaving(true);
    try {
      await updateWeeklyLeaderboardConfig(config);
      showSnackbar(
        "✅ Weekly leaderboard configuration updated successfully!",
        "success"
      );
      setIsDirty(false);
      setIsEditMode(false); // Exit edit mode after successful save
      setExpandedSection("summary"); // Keep summary accordion open
    } catch (error) {
      showSnackbar(
        error.message || "Failed to update weekly leaderboard configuration",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleBasicConfigChange = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsDirty(true);
  };

  const handleDivisionMultipliersChange = (newMultipliers: any) => {
    setConfig((prev) => ({
      ...prev,
      divisionMultipliers: newMultipliers,
    }));
    setIsDirty(true);
  };

  const handleLeagueConfigChange = (newLeagueConfig: any) => {
    setConfig((prev) => ({
      ...prev,
      leagueConfig: newLeagueConfig,
    }));
    setIsDirty(true);
  };

  const handleAccordionChange = (panel: any) => (_event, isExpanded) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  const handleEditMode = () => {
    setIsEditMode(true);
    setExpandedSection("basic"); // Start with basic configuration expanded
  };

  const handleCancelEdit = () => {
    if (isDirty) {
      const confirmCancel = window.confirm(
        "You have unsaved changes. Are you sure you want to cancel?"
      );
      if (!confirmCancel) return;
    }
    setIsEditMode(false);
    setExpandedSection("summary");
    setIsDirty(false);
    // Reset config to last saved state
    fetchConfig();
  };

  useEffect(() => {
    fetchConfig();
    fetchRewardPacks();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <ContentShell title="Weekly Leaderboard Configuration" loading={true}>
        <Box className={styles.loadingContainer}>
          <CircularProgress size={64} />
        </Box>
      </ContentShell>
    );
  }

  const hasErrors =
    errors.basic.length > 0 ||
    errors.divisionMultipliers.length > 0 ||
    errors.leagueConfig.length > 0;

  return (
    <ContentShell
      title="Weekly Leaderboard Configuration"
      actions={
        isEditMode ? (
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              startIcon={<CancelIcon />}
              style={{
                backgroundColor: "red",
                color: "white",
                borderRadius: "24px",
              }}
              onClick={handleCancelEdit}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={
                saving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              onClick={handleSave}
              disabled={saving || !isDirty}
              className={styles.saveButton}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        ) : (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleEditMode}
            className={styles.saveButton}
          >
            Edit Configuration
          </Button>
        )
      }
    >
      <Box className={styles.container}>
        {/* Error Alert */}
        {hasErrors && (
          <Alert severity="error" className={styles.errorAlert}>
            <Typography variant="subtitle2" fontWeight="600" gutterBottom>
              Configuration Issues Found:
            </Typography>
            {errors.basic.map((error, index) => (
              <Typography key={index} variant="body2">
                • Basic Settings: {error}
              </Typography>
            ))}
            {errors.divisionMultipliers.map((error, index) => (
              <Typography key={index} variant="body2">
                • Division Multipliers: {error}
              </Typography>
            ))}
            {errors.leagueConfig.map((error, index) => (
              <Typography key={index} variant="body2">
                • League Configuration: {error}
              </Typography>
            ))}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            {/* Configuration Summary - Only show when not in edit mode */}
            {!isEditMode && (
              <Accordion
                className={styles.accordion}
                expanded={expandedSection === "summary"}
                onChange={handleAccordionChange("summary")}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  className={styles.accordionSummary}
                >
                  <Box>
                    <Typography className={styles.accordionTitle}>
                      📊 Configuration Overview
                    </Typography>
                    <Typography className={styles.accordionSubtitle}>
                      Quick summary of all current settings
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <ConfigurationSummaryCards
                    config={config}
                    rewardPackOptions={rewardPackOptions}
                    onEdit={handleEditMode}
                  />
                </AccordionDetails>
              </Accordion>
            )}

            {/* Edit Sections - Only show when in edit mode */}
            {isEditMode && (
              <>
                {/* Basic Configuration */}
                <Accordion
                  className={styles.accordion}
                  expanded={expandedSection === "basic"}
                  onChange={handleAccordionChange("basic")}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className={styles.accordionSummary}
                  >
                    <Box>
                      <Typography className={styles.accordionTitle}>
                        ⚙️ Basic Configuration
                      </Typography>
                      <Typography className={styles.accordionSubtitle}>
                        Core XP and multiplier settings
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <BasicConfigurationEditor
                      config={config}
                      onChange={handleBasicConfigChange}
                      errors={errors.basic}
                    />
                  </AccordionDetails>
                </Accordion>

                {/* Division Multipliers */}
                <Accordion
                  className={styles.accordion}
                  expanded={expandedSection === "divisions"}
                  onChange={handleAccordionChange("divisions")}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className={styles.accordionSummary}
                  >
                    <Box>
                      <Typography className={styles.accordionTitle}>
                        🎯 Division Multipliers
                      </Typography>
                      <Typography className={styles.accordionSubtitle}>
                        XP multipliers for each division (1-10)
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <DivisionMultipliersEditor
                      data={config.divisionMultipliers}
                      onChange={handleDivisionMultipliersChange}
                      errors={errors.divisionMultipliers}
                    />
                  </AccordionDetails>
                </Accordion>

                {/* League Configuration */}
                <Accordion
                  className={styles.accordion}
                  expanded={expandedSection === "leagues"}
                  onChange={handleAccordionChange("leagues")}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className={styles.accordionSummary}
                  >
                    <Box>
                      <Typography className={styles.accordionTitle}>
                        🏆 League Configuration
                      </Typography>
                      <Typography className={styles.accordionSubtitle}>
                        Promotion/demotion rules and reward packs for each
                        league
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <LeagueConfigurationEditor
                      data={config.leagueConfig}
                      onChange={handleLeagueConfigChange}
                      rewardPackOptions={rewardPackOptions}
                      errors={errors.leagueConfig}
                    />
                  </AccordionDetails>
                </Accordion>
              </>
            )}
          </Grid>
        </Grid>

        {/* Floating Action Button for Save - Only show in edit mode */}
        <Zoom in={trigger && isDirty && !saving && isEditMode}>
          <Fab
            color="primary"
            className={styles.floatingActionButton}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <SaveIcon />
            )}
          </Fab>
        </Zoom>
      </Box>
    </ContentShell>
  );
};

export default WeeklyLeaderboardConfig;
